import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { useState } from 'react'
import { PopupState } from 'material-ui-popup-state/hooks'
import { toast } from 'sonner'
import Toast from '../Toast'
import { AxiosError } from 'axios'
import { HTTPValidationError } from '@common/config/api/client'
import { DeleteOutlineRounded } from '@mui/icons-material'
import { UsePatchTaskMutation } from '@features/reader_task_list_iam_view/pages/hooks/UsePatchTaskMutation'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 1,
}

export default function DeleteModal({
  taskIds,
  study_id,
  popupState,
}: {
  taskIds: string[]
  study_id: string
  popupState?: PopupState
}) {
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
    popupState?.close()
  }
  const { mutateAsync: deleteTask } = UsePatchTaskMutation()

  const handleDeleteTask = async (task_ids: string[], study_id: string) => {
    await deleteTask({
      query: { study_id: study_id, read_task_ids: task_ids },
      body: {
        is_deleted: true,
      },
    })
      .then(() => {
        toast(<Toast message="Your request was sent successfully" variant="success" data-testid="success-toast" />)
        handleClose()
      })
      .catch((error: AxiosError<HTTPValidationError>) => {
        const errorMessage = Array.isArray(error.response?.data?.detail)
          ? error.response?.data?.detail.map((err) => err.msg).join(', ')
          : error.response?.data?.detail || error.message
        toast(<Toast message={errorMessage} variant="error" data-testid="error-toast" />)
      })
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="delete-task-button"
        tabIndex={0}
      >
        <DeleteOutlineRounded
          fontSize="small"
          sx={{
            color: 'text.secondary',
            cursor: 'pointer',
          }}
        />
        {popupState && <Typography variant="body1">Delete</Typography>}
      </Box>
      <Modal
        open={open}
        data-testid="delete-modal"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              alignSelf: 'stretch',
              width: 600,
            }}
          >
            <Typography variant="h6" color="text.primary" id="delete-modal-title">
              Delete task?
            </Typography>
            <Typography variant="body1" color="text.secondary" id="delete-modal-description">
              The task will be deleted permanently, and the reader will be informed.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: 2,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-delete-task-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handleDeleteTask(taskIds, study_id)}
              data-testid="confirm-delete-task-button"
            >
              Delete task
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
